import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { WESTGARD_RULE_WORDS, WESTGARD_RULES, type WestgardRule } from '@wodi/domain';
import type { QcLotView, QcMaterialView, QcSetupView } from '@wodi/shared';
import {
  Button,
  Checkbox,
  DateField,
  Dialog,
  DialogActions,
  Notice,
  Select,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  createQcLot,
  createQcMaterial,
  qcKeys,
  qcSetupQuery,
  retireQcTarget,
  setQcRules,
  setQcTarget,
} from '../api/lab-qc.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from '../lab/lab.module.css';
import { formatDateOnly } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { LabSettingsFrame, QueryState } from './settings-lab.tsx';

// Settings > Lab > Quality control (docs/plans/phase3-lab-qc-and-microbiology.md §1.1.1–4):
// control materials and their lots, the mean and SD the lab enters for each lot, analyte and
// analyzer, and which Westgard rules are on. Wodi never computes or suggests a target.

const NO_ANALYZER = 'none';

function useRefresh() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: qcKeys.all });
}

export function LabQcSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const setup = useQuery(qcSetupQuery);
  const canEdit = session !== null && can(session, 'lab.qc.manage');
  if (session === null || !can(session, 'lab.qc.read')) {
    return (
      <LabSettingsFrame title="Lab settings">
        <Notice tone="info">
          You don't have permission to see QC set-up. Ask an administrator if you need it.
        </Notice>
      </LabSettingsFrame>
    );
  }
  return (
    <LabSettingsFrame title="Lab settings">
      <Notice tone="info">
        Enter each target's mean and SD from the control's insert or your lab's own runs, with where
        they came from. The default Westgard rules are provisional until the lab lead confirms them.
      </Notice>
      <QueryState query={setup} what="QC set-up" />
      {setup.data === undefined ? null : (
        <>
          <Materials setup={setup.data} canEdit={canEdit} />
          <Rules setup={setup.data} canEdit={canEdit} />
        </>
      )}
    </LabSettingsFrame>
  );
}

function Materials({ setup, canEdit }: { setup: QcSetupView; canEdit: boolean }) {
  const refresh = useRefresh();
  const online = useOnline();
  const [name, setName] = useState('');
  const [level, setLevel] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const add = useMutation({
    mutationFn: () =>
      createQcMaterial({
        name: name.trim(),
        levelLabel: level.trim(),
        manufacturer: manufacturer.trim() === '' ? null : manufacturer.trim(),
      }),
    onSuccess: async () => {
      toasts.show({ title: 'QC material added' });
      setName('');
      setLevel('');
      setManufacturer('');
      await refresh();
    },
  });
  return (
    <section className={styles.section} aria-labelledby="qc-materials">
      <h2 id="qc-materials" className={styles.sectionHeading}>
        Control materials
      </h2>
      {setup.materials.length === 0 ? <p>No control materials yet.</p> : null}
      {setup.materials.map((m) => (
        <MaterialBlock key={m.id} material={m} setup={setup} canEdit={canEdit} />
      ))}
      {canEdit ? (
        <form
          className={styles.toolbar}
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim() !== '' && level.trim() !== '') add.mutate();
          }}
        >
          <TextField label="Material" value={name} onChange={setName} />
          <TextField
            label="Level"
            description="For example Level 1."
            value={level}
            onChange={setLevel}
          />
          <TextField
            label="Manufacturer (optional)"
            value={manufacturer}
            onChange={setManufacturer}
          />
          <Button
            type="submit"
            isDisabled={!online || name.trim() === '' || level.trim() === ''}
            isPending={add.isPending}
            pendingLabel="Adding…"
          >
            Add material
          </Button>
        </form>
      ) : null}
      {add.isError ? (
        <Notice tone="error">{errorText(add.error, "The material wasn't added.")}</Notice>
      ) : null}
    </section>
  );
}

function MaterialBlock({
  material,
  setup,
  canEdit,
}: {
  material: QcMaterialView;
  setup: QcSetupView;
  canEdit: boolean;
}) {
  const refresh = useRefresh();
  const online = useOnline();
  const [lotNumber, setLotNumber] = useState('');
  const [expiresOn, setExpiresOn] = useState<string | null>(null);
  const [controlId, setControlId] = useState('');
  const addLot = useMutation({
    mutationFn: () =>
      createQcLot(material.id, {
        lotNumber: lotNumber.trim(),
        expiresOn: expiresOn ?? '',
        analyzerControlId: controlId.trim() === '' ? null : controlId.trim(),
      }),
    onSuccess: async () => {
      toasts.show({ title: 'Lot added' });
      setLotNumber('');
      setExpiresOn(null);
      setControlId('');
      await refresh();
    },
  });
  return (
    <div className={styles.stack}>
      <h3 className={styles.entryHeading}>
        {`${material.name}, ${material.levelLabel}`}
        {material.manufacturer === null ? '' : ` (${material.manufacturer})`}
      </h3>
      {material.lots.length === 0 ? <p className={styles.meta}>No lots yet.</p> : null}
      {material.lots.map((lot) => (
        <LotBlock key={lot.id} lot={lot} setup={setup} canEdit={canEdit} />
      ))}
      {canEdit ? (
        <form
          className={styles.toolbar}
          aria-label={`Add a lot of ${material.name}, ${material.levelLabel}`}
          onSubmit={(event) => {
            event.preventDefault();
            if (lotNumber.trim() !== '' && expiresOn !== null) addLot.mutate();
          }}
        >
          <TextField label="Lot number" value={lotNumber} onChange={setLotNumber} />
          <DateField label="Expires" value={expiresOn} onChange={setExpiresOn} />
          <TextField
            label="Analyzer control ID (optional)"
            description="The sample ID the analyzer reports for this control."
            value={controlId}
            onChange={setControlId}
          />
          <Button
            type="submit"
            isDisabled={!online || lotNumber.trim() === '' || expiresOn === null}
            isPending={addLot.isPending}
            pendingLabel="Adding…"
          >
            Add lot
          </Button>
        </form>
      ) : null}
      {addLot.isError ? (
        <Notice tone="error">{errorText(addLot.error, "The lot wasn't added.")}</Notice>
      ) : null}
    </div>
  );
}

function LotBlock({
  lot,
  setup,
  canEdit,
}: {
  lot: QcLotView;
  setup: QcSetupView;
  canEdit: boolean;
}) {
  const refresh = useRefresh();
  const online = useOnline();
  const [adding, setAdding] = useState(false);
  const retire = useMutation({
    mutationFn: (targetId: string) => retireQcTarget(targetId),
    onSuccess: async () => {
      toasts.show({ title: 'Target retired' });
      await refresh();
    },
  });
  return (
    <div className={styles.stack}>
      <p>
        {'Lot '}
        <span className="wodi-id">{lot.lotNumber}</span>
        {`, expires ${formatDateOnly(`${lot.expiresOn}T12:00:00Z`)}`}
        {lot.analyzerControlId === null
          ? ''
          : `, analyzer control ID ${lot.analyzerControlId}`}{' '}
        {lot.expired ? <Tag state="attention">Expired</Tag> : null}
        {lot.status === 'inactive' ? <Tag state="neutral">Not in use</Tag> : null}
      </p>
      <ul className={styles.list}>
        {lot.targets.map((t) => (
          <li key={t.id} className={styles.listRow}>
            <span>{`${t.analyte.name} on ${t.analyzer?.name ?? 'no analyzer'}`}</span>
            <span>{`Mean ${t.mean}, SD ${t.sd}${t.analyte.unit === null ? '' : ` ${t.analyte.unit}`}. ${t.source}`}</span>
            {canEdit ? (
              <Button
                dense
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  retire.mutate(t.id);
                }}
              >
                Retire target
              </Button>
            ) : (
              <span />
            )}
          </li>
        ))}
      </ul>
      {lot.targets.length === 0 ? (
        <p className={styles.meta}>No targets for this lot yet.</p>
      ) : null}
      {canEdit && lot.status === 'active' ? (
        <div className={styles.actions}>
          <Button
            dense
            isDisabled={!online}
            onPress={() => {
              setAdding(true);
            }}
          >
            Set a target
          </Button>
        </div>
      ) : null}
      {retire.isError ? (
        <Notice tone="error">{errorText(retire.error, "The target wasn't retired.")}</Notice>
      ) : null}
      {adding ? (
        <TargetDialog
          lot={lot}
          setup={setup}
          onClose={() => {
            setAdding(false);
          }}
        />
      ) : null}
    </div>
  );
}

function TargetDialog({
  lot,
  setup,
  onClose,
}: {
  lot: QcLotView;
  setup: QcSetupView;
  onClose: () => void;
}) {
  const refresh = useRefresh();
  const [analyteId, setAnalyteId] = useState<string | null>(null);
  const [analyzerId, setAnalyzerId] = useState<string>(NO_ANALYZER);
  const [mean, setMean] = useState('');
  const [sd, setSd] = useState('');
  const [source, setSource] = useState('');
  const [tried, setTried] = useState(false);
  const analyte = setup.analytes.find((a) => a.id === analyteId);
  const save = useMutation({
    mutationFn: () =>
      setQcTarget(lot.id, {
        analyteId: analyteId ?? '',
        analyzerId: analyzerId === NO_ANALYZER ? null : analyzerId,
        mean: mean.trim(),
        sd: sd.trim(),
        source: source.trim(),
      }),
    onSuccess: async () => {
      toasts.show({ title: 'Target set' });
      await refresh();
      onClose();
    },
  });
  const ready =
    analyteId !== null && mean.trim() !== '' && sd.trim() !== '' && source.trim().length >= 3;
  return (
    <Dialog
      title={`Set a target for lot ${lot.lotNumber}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          setTried(true);
          if (ready) save.mutate();
        }}
      >
        <Select
          label="Analyte"
          options={setup.analytes.map((a) => ({ id: a.id, label: a.name }))}
          value={analyteId}
          onChange={(v) => {
            setAnalyteId(v === null ? null : String(v));
          }}
          {...(tried && analyteId === null ? { errorMessage: 'Choose the analyte.' } : {})}
        />
        <Select
          label="Analyzer"
          options={[
            { id: NO_ANALYZER, label: 'No analyzer (bench, or results by hand)' },
            ...setup.analyzers.map((a) => ({ id: a.id, label: a.name })),
          ]}
          value={analyzerId}
          onChange={(v) => {
            setAnalyzerId(v === null ? NO_ANALYZER : String(v));
          }}
        />
        <TextField
          label="Mean"
          {...(analyte?.unit == null ? {} : { unit: analyte.unit })}
          value={mean}
          onChange={setMean}
          {...(tried && mean.trim() === '' ? { errorMessage: 'Enter the mean.' } : {})}
        />
        <TextField
          label="SD"
          {...(analyte?.unit == null ? {} : { unit: analyte.unit })}
          value={sd}
          onChange={setSd}
          {...(tried && sd.trim() === '' ? { errorMessage: 'Enter the SD.' } : {})}
        />
        <TextField
          label="Source"
          description="Where the numbers came from, for example the insert or your own runs."
          value={source}
          onChange={setSource}
          {...(tried && source.trim().length < 3
            ? { errorMessage: 'Say where the numbers came from.' }
            : {})}
        />
        <p className={styles.meta}>
          Setting a target retires the current one for this analyte and analyzer.
        </p>
        {save.isError ? (
          <Notice tone="error">{errorText(save.error, "The target wasn't set.")}</Notice>
        ) : null}
        <DialogActions>
          <Button variant="quiet" onPress={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isPending={save.isPending} pendingLabel="Saving…">
            Set target
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function Rules({ setup, canEdit }: { setup: QcSetupView; canEdit: boolean }) {
  return (
    <section className={styles.section} aria-labelledby="qc-rules">
      <h2 id="qc-rules" className={styles.sectionHeading}>
        Westgard rules
      </h2>
      {setup.ruleSettings.length === 0 ? (
        <p>Rules can be chosen for an analyte once it has a target.</p>
      ) : null}
      {setup.ruleSettings.map((r) => (
        <RuleRow key={r.analyteId} setting={r} canEdit={canEdit} />
      ))}
    </section>
  );
}

function RuleRow({
  setting,
  canEdit,
}: {
  setting: QcSetupView['ruleSettings'][number];
  canEdit: boolean;
}) {
  const refresh = useRefresh();
  const online = useOnline();
  const save = useMutation({
    mutationFn: (rules: WestgardRule[]) => setQcRules(setting.analyteId, rules),
    onSuccess: async () => {
      toasts.show({ title: 'Rules saved' });
      await refresh();
    },
  });
  return (
    <fieldset className={styles.stack}>
      <legend>
        {setting.analyteName}{' '}
        {setting.isDefault ? <Tag state="pending">Provisional default</Tag> : null}
      </legend>
      <div className={styles.actions}>
        {WESTGARD_RULES.map((rule) => (
          <Checkbox
            key={rule}
            isSelected={setting.rules.includes(rule)}
            isDisabled={!canEdit || !online || save.isPending}
            description={WESTGARD_RULE_WORDS[rule]}
            onChange={(on) => {
              save.mutate(on ? [...setting.rules, rule] : setting.rules.filter((x) => x !== rule));
            }}
          >
            {rule}
          </Checkbox>
        ))}
      </div>
      {save.isError ? (
        <Notice tone="error">{errorText(save.error, "The rules weren't saved.")}</Notice>
      ) : null}
    </fieldset>
  );
}
